import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Airplane, ArrowLeft, Package } from "@phosphor-icons/react";

export default function NewShipment() {
  const navigate = useNavigate();
  const [drives, setDrives] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    awb_number: "",
    doc_date: new Date().toISOString().slice(0, 10),
    customs_no_1: "",
    customs_no_2: "",
    consignee: "",
    drive: "C",
    notes: "",
  });

  useEffect(() => {
    api.get("/drives").then(({ data }) => {
      setDrives(data);
      if (data?.length) setForm((f) => ({ ...f, drive: data[0].letter }));
    }).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/shipments", form);
      toast.success(`Shipment ${form.awb_number} created — now attach documents`);
      navigate(`/shipments/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <button onClick={() => navigate("/shipments")} data-testid="back-btn"
        className="text-xs text-zinc-500 font-mono flex items-center gap-1 hover:text-zinc-900 transition-colors duration-150 mb-4">
        <ArrowLeft size={12} /> BACK TO SHIPMENTS
      </button>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Step 1 of 2</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950 flex items-center gap-3">
          <Airplane size={40} weight="duotone" className="text-[#FF4F00]" />
          New shipment
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-xl">
          Enter shipment header info. On the next screen you'll upload the required documents (AWB, Invoice, Permit, Terminal Charges, Customs Form).
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 max-w-3xl" data-testid="shipment-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="AWB Number *">
            <input required value={form.awb_number} onChange={upd("awb_number")}
              placeholder="176-12345678" autoFocus
              className="w-full border border-zinc-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="awb-input" />
          </Field>
          <Field label="Date">
            <input type="date" value={form.doc_date} onChange={upd("doc_date")}
              className="w-full border border-zinc-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="date-input" />
          </Field>
          <Field label="Customs No. 1">
            <input value={form.customs_no_1} onChange={upd("customs_no_1")}
              placeholder="Primary / export"
              className="w-full border border-zinc-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="customs1-input" />
          </Field>
          <Field label="Customs No. 2 (for import)">
            <input value={form.customs_no_2} onChange={upd("customs_no_2")}
              placeholder="Secondary / import"
              className="w-full border border-zinc-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="customs2-input" />
          </Field>
          <Field label="Consignee">
            <input value={form.consignee} onChange={upd("consignee")}
              placeholder="Company / customer"
              className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="consignee-input" />
          </Field>
          <Field label="Drive">
            <select value={form.drive} onChange={upd("drive")}
              className="w-full border border-zinc-200 px-3 py-2 text-sm bg-white font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="drive-input">
              {drives.map((d) => (
                <option key={d.letter} value={d.letter}>{`${d.letter}:  ${d.label ? `(${d.label})` : ""}`}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Notes">
            <textarea rows={3} value={form.notes} onChange={upd("notes")}
              placeholder="Any remarks…"
              className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
              data-testid="notes-input" />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button type="button" onClick={() => navigate("/shipments")}
            className="px-4 py-2.5 text-sm border border-zinc-200 bg-white hover:bg-zinc-100 transition-colors duration-150"
            data-testid="cancel-btn">Cancel</button>
          <button type="submit" disabled={busy}
            className="px-4 py-2.5 text-sm bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-60 transition-colors duration-150 flex items-center gap-2"
            data-testid="create-shipment-btn">
            <Package size={16} /> {busy ? "Creating…" : "Create & Attach Docs →"}
          </button>
        </div>
      </form>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
